/**
 * The warning before walking away from a form which was written in.
 *
 * A guarded form is photographed when the page loads and compared against
 * that photograph whenever the visitor tries to leave. Leaving by a link asks
 * the portal's own question; leaving by closing the tab can only raise the
 * browser's own, which is all a page is allowed to do there.
 *
 * Saving, deleting and every other submission disarm the guard: the form is
 * on its way to the server, so its changes are not about to be lost.
 */

import { askConfirmation } from "./modal";
import { text } from "../core/i18n";
import { queryAll } from "../core/dom";

/** Forms which are watched. */
const GUARDED = "form[data-guard-unsaved]";

const watched = new Map<HTMLFormElement, string>();

let leaving = false;
let wired = false;

/**
 * Return the values of `form` as one string.
 *
 * A file carries its name rather than its bytes: picking a file is a change
 * worth warning about, and reading it back would be neither cheap nor
 * synchronous.
 */
export function snapshot(form: HTMLFormElement): string {
    const parts: string[] = [];

    for (const [key, value] of new FormData(form).entries()) {
        parts.push(`${key}=${value instanceof File ? value.name : value}`);
    }

    return parts.join("&");
}

/** Let the next navigation through without a question. */
export function allowUnload(): void {
    leaving = true;
}

/** Return whether any watched form holds something which was not saved. */
export function hasUnsavedChanges(forms: ReadonlyMap<HTMLFormElement, string>): boolean {
    if (leaving) {
        return false;
    }

    for (const [form, clean] of forms) {
        if (snapshot(form) !== clean) {
            return true;
        }
    }

    return false;
}

/** Return whether `link` really takes the visitor off this page. */
export function leadsAway(link: HTMLAnchorElement, event: MouseEvent): boolean {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
        return false;
    }

    const href = link.getAttribute("href") ?? "";
    if (!href || href.startsWith("#") || link.target === "_blank" || link.hasAttribute("download")) {
        return false;
    }

    return true;
}

/**
 * Grey the save button of `form` out while it holds nothing new.
 *
 * A button which cannot be pressed says more than one which can but does
 * nothing. It is enabled again by the first thing typed, and by anything a
 * script writes and announces - the two events below are what every component
 * of the portal raises after it changes a field.
 *
 * A button carrying its own `formaction` is left alone: it is not saving this
 * form, it is one press doing one thing at another address, and it works
 * whether or not anybody has typed anything.
 */
export function keepSaveInStep(form: HTMLFormElement, clean: string): void {
    // A form which also waits for its fields decides both there, so the two
    // guards cannot take turns switching the same button on and off.
    if (form.hasAttribute("data-ready")) {
        return;
    }

    const buttons = queryAll<HTMLButtonElement>("button[type=submit]", form).filter(
        // A button which belongs to another form by its `form` attribute only
        // stands inside this one; it sends that other form.
        (button) => !button.hasAttribute("formaction") && button.form === form,
    );

    if (buttons.length === 0) {
        return;
    }

    const refresh = (): void => {
        const untouched = snapshot(form) === clean;

        for (const button of buttons) {
            button.disabled = untouched;
        }
    };

    form.addEventListener("input", refresh);
    form.addEventListener("change", refresh);
    refresh();
}

/** Watch every guarded form of the current page. */
export function initUnsavedGuards(): void {
    // A page which has just loaded has nothing on its way out, and the forms
    // it holds are the only ones worth watching. Both are read again rather
    // than added to, so wiring the guard twice watches one page, not two.
    leaving = false;
    watched.clear();

    for (const form of queryAll<HTMLFormElement>(GUARDED)) {
        const clean = snapshot(form);
        watched.set(form, clean);
        keepSaveInStep(form, clean);
    }

    if (watched.size === 0 || wired) {
        return;
    }

    wired = true;

    // Every submission is a departure with the changes in hand, whichever
    // form it came from - the editing one, or the small one which deletes.
    document.addEventListener("submit", (event: Event) => {
        if (!event.defaultPrevented) {
            allowUnload();
        }
    });

    document.addEventListener("click", (event: MouseEvent) => {
        const link = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>("a[href]");
        if (!link || !leadsAway(link, event) || !hasUnsavedChanges(watched)) {
            return;
        }

        event.preventDefault();

        void askConfirmation(text("js.unsaved.question", "Máte neuložené zmeny, ktoré sa stratia."), {
            title: text("js.unsaved.title", "Neuložené zmeny"),
            confirm: text("js.unsaved.leave", "Odísť bez uloženia"),
            cancel: text("js.unsaved.stay", "Zostať"),
            danger: true,
        }).then((agreed) => {
            if (agreed) {
                allowUnload();
                window.location.assign(link.href);
            }
        });
    });

    // Closing the tab or reloading reaches no click handler, and a page may
    // not word this one itself - the browser writes it.
    window.addEventListener("beforeunload", (event: BeforeUnloadEvent) => {
        if (hasUnsavedChanges(watched)) {
            event.preventDefault();
        }
    });
}
